// src/pages/PublicEpkPage.tsx

import { useEffect, useState } from 'react';
import { getPublicEpk, logEpkEvent } from '@/api/epkPublic';
import type { PublicEpkData } from '@/types/epk';
import EpkRenderer from '@/components/kit/epk/EpkRenderer';
import EpkReportModal from '@/components/kit/epk/EpkReportModal';

interface PublicEpkPageProps {
  slug: string;
}

export default function PublicEpkPage({ slug }: PublicEpkPageProps) {
  const [pageData, setPageData] = useState<PublicEpkData | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!slug) return;

    // Extract tracking ID from query string
    const params = new URLSearchParams(window.location.search);
    const trackingId = params.get('id') || undefined;

    const fetchPage = async () => {
      try {
        const data = await getPublicEpk(slug);
        setPageData(data);

        // Log page view
        logEpkEvent(slug, {
          event_type: 'page_view',
          referrer: document.referrer || undefined,
          tracking_id: trackingId,
        }).catch(() => {}); // Fire and forget
      } catch (err: any) {
        setError(err?.error || { code: 'NOT_FOUND', message: 'Page not found' });
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [slug]);

  // Set the browser tab title
  useEffect(() => {
    if (pageData?.artist_name) {
      document.title = `${pageData.artist_name} — EPK`;
    } else if (error) {
      document.title = error.code === 'SUSPENDED' ? 'Page Not Available' : 'Page Not Found';
    }
  }, [pageData, error]);

  const handleLinkClick = (linkId: string, label: string) => {
    if (!slug) return;
    logEpkEvent(slug, {
      event_type: 'link_click',
      link_id: linkId,
      link_label: label,
    }).catch(() => {});
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-900">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-white" />
      </div>
    );
  }

  // Error states
  if (error) {
    if (error.code === 'SUSPENDED') {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-900 px-6 text-center">
          <svg className="mb-4 h-16 w-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h1 className="text-xl font-bold text-white">Page Not Available</h1>
          <p className="mt-2 text-sm text-gray-400">This page has been removed for violating our terms of service.</p>
          <a href="https://www.bandjerry.com" className="mt-6 text-sm text-yellow-400 hover:underline">Powered by BandJerry</a>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-900 px-6 text-center">
        <svg className="mb-4 h-16 w-16 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
        <h1 className="text-xl font-bold text-white">Page Not Found</h1>
        <p className="mt-2 text-sm text-gray-400">This page doesn't exist or isn't available right now.</p>
        <a href="https://www.bandjerry.com" className="mt-6 text-sm text-yellow-400 hover:underline">Create your own — Powered by BandJerry</a>
      </div>
    );
  }

  if (!pageData) return null;

  return (
    <div className="min-h-screen">
      <EpkRenderer
        data={pageData}
        onLinkClick={handleLinkClick}
      />

      {/* Report button - fixed at bottom */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={() => setShowReport(true)}
          className="rounded-full bg-black/30 px-3 py-1.5 text-xs text-white/50 backdrop-blur-sm transition-colors hover:text-white/80"
        >
          Report
        </button>
      </div>

      {showReport && slug && (
        <EpkReportModal slug={slug} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}
